/* eslint-disable no-empty */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState, useEffect, useRef } from "react";
import Select from "react-select";
import { FaVideo, FaPhoneAlt } from "react-icons/fa";

import {
  addDays,
  addMinutes,
  format,
  isSameMonth,
  startOfMonth,
} from "date-fns";

import moment from "moment-timezone";
import { timezoneFormatter } from "../../../utils/staticData";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

import axios from "axios";
import MultiStepLeadForm from "./MultiStepLeadForm";
import "./BookAppointmentFormUpdatedUi.scss";

const cleintUrlProd = "https://app.unclekam.com";
const cleintUrlStaging = "https://staging.unclekam.com";

// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const ANNUAL_INCOMES = [
  { id: 1, name: "$12,000-$49,999" },
  { id: 2, name: "$50,000-$99,999" },
  { id: 3, name: "$100,000-$249,999" },
  { id: 4, name: "$250,000-$499,999" },
  { id: 5, name: "$500,000+" },
];

// ---- EXACTLY MATCH OLD: 45-minute slots
const SLOT_MINUTES = 45;
const UPCOMING_DAYS_TO_SHOW = 3;

/* =========================
   TIMEZONE OPTIONS — match old list/labels and default
   ========================= */

const buildUSTzOptions = () => {
  const top = [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
  ];
  const us = moment.tz.zonesForCountry("US") || [];
  const unique = Array.from(new Set([...top, ...us]));
  return unique.map((tz) => ({ value: tz, label: timezoneFormatter(tz) }));
};

const selectedLabel = (tz) =>
  `${timezoneFormatter(tz)} (${dayjs().tz(tz).format("h:mma")})`;

/* =========================
   SLOT/DATE HELPERS — same pipeline as old file
   (provider local -> UTC -> viewer tz for display)
   ========================= */

const generateTicks = (start, end, stepMinutes = SLOT_MINUTES) => {
  const out = [];
  let cur = new Date(start);
  while (cur < end) {
    out.push(cur);
    cur = addMinutes(cur, stepMinutes);
  }
  return out;
};

const parseTimeHM = (str) => {
  if (!str) return null;
  const s = String(str).trim();

  let m = s.match(/^(\d{1,2}):(\d{2})\s*([ap]m)$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = m[3].toLowerCase();
    if (ap === "pm" && h !== 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
    return { hour: h, minute: min };
  }

  m = s.match(/^(\d{1,2})\s*([ap]m)$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const ap = m[2].toLowerCase();
    if (ap === "pm" && h !== 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
    return { hour: h, minute: 0 };
  }

  m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) return { hour: parseInt(m[1], 10), minute: parseInt(m[2], 10) };

  m = s.match(/^(\d{1,2})$/);
  if (m) return { hour: parseInt(m[1], 10), minute: 0 };

  return null;
};

const dateAtLocalTime = (jsDate, timeStr, providerTZ) => {
  if (!jsDate || !timeStr || !providerTZ) return null;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: providerTZ });
  } catch {
    providerTZ = "UTC";
  }

  const parts = parseTimeHM(timeStr);
  if (!parts) return null;

  const y = jsDate.getFullYear();
  const m = jsDate.getMonth() + 1;
  const d = jsDate.getDate();

  const baseLocal = dayjs.tz(
    `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(
      d
    ).padStart(2, "0")}T00:00`,
    providerTZ
  );
  if (!baseLocal.isValid()) return null;

  const dt = baseLocal.add(parts.hour, "hour").add(parts.minute, "minute");
  return dt.isValid() ? dt.utc().toDate() : null;
};

const buildSlotsForDate = (date, payload, selectedTZ) => {
  const providerTZ = payload?.settings?.timezone;
  const weekdayName = WEEKDAYS[date.getDay()];
  const dayRanges = payload?.availability?.time_ranges?.[weekdayName];
  if (!providerTZ || !Array.isArray(dayRanges) || dayRanges.length === 0)
    return [];

  const slots = [];
  for (const range of dayRanges) {
    if (!range?.from || !range?.to) continue;

    const startUtc = dateAtLocalTime(date, range.from, providerTZ);
    const endUtc = dateAtLocalTime(date, range.to, providerTZ);
    if (!startUtc || !endUtc || !(endUtc > startUtc)) continue;

    const ticks = generateTicks(startUtc, endUtc, SLOT_MINUTES);
    for (const utcDate of ticks) {
      const label = dayjs(utcDate).utc().tz(selectedTZ).format("h:mm A");
      slots.push({ utc: utcDate, iso: utcDate.toISOString(), label });
    }
  }
  return slots;
};

const buildUpcomingDaysWithSlots = (payload, selectedTZ, maxDays) => {
  if (!payload) return [];
  const out = [];
  const today = new Date();
  for (let i = 1; i <= 180 && out.length < maxDays; i++) {
    const d = addDays(today, i);
    const slots = buildSlotsForDate(d, payload, selectedTZ);
    if (slots.length) out.push({ date: d, slots });
  }
  return out;
};

const BookingSchedulerV2 = ({
  hasPayload,
  taxProAvailability,
  tzOptions,
  selectedTZ,
  setSelectedTZ,
  selectedSlotIso,
  setSelectedSlotIso,
  setSelectedDate,
  appointmentType,
  setAppointmentType,
  loading,
  status,
  message,
  primaryButtonText,
  onPrimaryClick,
}) => {
  const days = useMemo(() => {
    if (!hasPayload) return [];
    return buildUpcomingDaysWithSlots(
      taxProAvailability,
      selectedTZ.value,
      UPCOMING_DAYS_TO_SHOW
    );
  }, [hasPayload, taxProAvailability, selectedTZ.value]);

  return (
    <div className="uk-booking-v2 container py-3">
      <div style={{ color: "#4B57E7" }} className="mt-2 fw-semibold text-center" >Choose only one</div>

      <div className="d-flex justify-content-center mt-2">
        <div className="uk-booking-v2__tz">
          <select
            className="form-select uk-booking-v2__tz-select"
            value={selectedTZ.value}
            onChange={(e) => {
              const next = tzOptions.find((o) => o.value === e.target.value);
              if (next) setSelectedTZ(next);
            }}
          >
            {tzOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {selectedLabel(o.value)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Status messages */}
      <div className="mt-3">
        {status === "success" && (
          <div className="alert alert-success py-2 mb-2">{message}</div>
        )}
        {status === "error" && (
          <div className="alert alert-danger py-2 mb-2">{message}</div>
        )}
      </div>

      {/* Days + time slots */}
      <div className="uk-booking-v2__days mt-2">
        {days.map((dObj) => (
          <div key={dObj.date.toISOString()} className="mb-3">
            <div className="text-dark fw-semibold ">
              {format(dObj.date, "EEEE, MMMM d, yyyy")}
            </div>

            <div className="d-flex flex-wrap gap-2 mt-2">
              {dObj.slots.map((s) => {
                const active = selectedSlotIso === s.iso;
                return (
                  <button
                    key={s.iso}
                    type="button"
                    className={[
                      "btn",
                      active ? "uk-booking-v2__slot-btn-active" : "btn",
                      "uk-booking-v2__slot-btn",
                    ].join(" ")}
                    onClick={() => {
                      setSelectedDate(dObj.date);
                      setSelectedSlotIso(s.iso);
                    }}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {!days.length && (
          <div className="text-muted text-center py-4">
            No available times right now.
          </div>
        )}
      </div>

<div className="uk-appt-checkbox-row text-center justify-content-center mt-3">
  <div
    className={`uk-appt-checkbox ${
      appointmentType === "video" ? "active" : ""
    }`}
    onClick={() => setAppointmentType("video")}
  >
    <span className="uk-appt-box">
      {appointmentType === "video" ? "✓" : ""}
    </span>
    <span className="uk-appt-text fw-semibold">Video Appointment</span>
  </div>

  <div
    className={`uk-appt-checkbox ${
      appointmentType === "phone" ? "active" : ""
    }`}
    onClick={() => setAppointmentType("phone")}
  >
    <span className="uk-appt-box">
      {appointmentType === "phone" ? "✓" : ""}
    </span>
    <span className="uk-appt-text fw-semibold">Phone Appointment</span>
  </div>
</div>


      {/* Primary CTA */}
      <div className="d-flex justify-content-center mt-4">
        <button
          type="button"
          className="btn btn-primary btn-lg uk-booking-v2__cta"
          disabled={!selectedSlotIso || loading}
          onClick={onPrimaryClick}
        >
          {loading && (
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            />
          )}
          {primaryButtonText}
        </button>
      </div>

      {!selectedSlotIso && (
        <div className="text-center text-muted mt-2 small">
          Pick a time to continue
        </div>
      )}
    </div>
  );
};

const BookAppointmentFormUpdatedUi = ({ qa_mode, auto_login = true, proxyUrl }) => {
  const tzOptions = useMemo(buildUSTzOptions, []);
  const PROXY_URL =
    proxyUrl ||
    window.__UK_PROXY_URL__ ||
    `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

  const initialTz = useMemo(() => {
    const guess = moment.tz.guess();
    return guess && guess.startsWith("America/") ? guess : "America/Adak";
  }, []);

  const [selectedTZ, setSelectedTZ] = useState(() => {
    const opt =
      tzOptions.find((o) => o.value === initialTz) || {
        value: initialTz,
        label: timezoneFormatter(initialTz),
      };
    return opt;
  });

  const zipRef = useRef(null);

  // keep these (logic relies on them)
  const [monthCursor, setMonthCursor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(addDays(new Date(), 1));
  const [selectedSlotIso, setSelectedSlotIso] = useState(null);

  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  const [formData, setFormData] = useState(() => ({
    options: [],
    income: "",
    zip: "",
  }));

  const [taxProAvailability, setTaxProAvailability] = useState(null);

  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const email = params.get("email");
  const pro_id = params.get("pro_id");

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [appointmentType, setAppointmentType] = useState("video");
  const [userCheck, setUserCheck] = useState({});
  const [createUserSteps, setCreateUserSteps] = useState(1);
  const [createLeadSteps, setCreateLeadSteps] = useState(1);

  const qa = useMemo(
    () => (typeof qa_mode === "boolean" ? qa_mode : params.has("qa_mode")),
    [qa_mode, params]
  );
  const CLIENT_URL = qa ? cleintUrlStaging : cleintUrlProd;

  function convertTimeIntoSselectedTZ(timezoneAssign, targetTimeZone, date, formatStr) {
    const currentTimeWithTZ = moment.tz(
      moment(date).format("YYYY-MM-DDTHH:mm:ss"),
      timezoneAssign
    );
    return moment(currentTimeWithTZ).tz(targetTimeZone).format(formatStr || "YYYY-MM-DD HH:mm:ss");
  }

  const getUserTZ = (u, fallback = "UTC") => u?.settings?.timezone || fallback;

  const createAppointment = async (lead, isMultyStep) => {
    setLoading(true);
    try {
      if (!selectedSlotIso) {
        setStatus("error");
        setMessage("Pick a time first.");
        return;
      }
      if (!lead) {
        setStatus("error");
        setMessage("Lead not loaded yet.");
        return;
      }

      const duration = 45;
      const startUtcIso = selectedSlotIso;
      const endUtcIso = dayjs(startUtcIso).add(duration, "minute").toISOString();

      const proTZ =
        getUserTZ(lead?.TaxPro?.user) ||
        getUserTZ(lead?.taxPro?.user) ||
        getUserTZ(taxProAvailability) ||
        "UTC";

      const startDateLocal = convertTimeIntoSselectedTZ(
        "UTC",
        proTZ,
        startUtcIso,
        "YYYY-MM-DD HH:mm:ss"
      );
      const endDateLocal = convertTimeIntoSselectedTZ(
        "UTC",
        proTZ,
        endUtcIso,
        "YYYY-MM-DD HH:mm:ss"
      );

      const appointmentObj = { timezone: proTZ, startDate: startDateLocal, endDate: endDateLocal, duration };

      if (appointmentType === "video") {
        const meetingData = {
          taxProEmail: lead?.taxPro?.user?.email || lead?.user?.email,
          email: lead?.user?.email,
          startTime: moment(selectedSlotIso).tz(proTZ).format("YYYY-MM-DD HH:mm:ss"),
          duration,
          timezone: proTZ,
        };

        await axios
          .post(
            PROXY_URL,
            {
              path: "/createZoomMeeting",
              method: "PUT",
              qa_mode: qa,
              data: meetingData,
            },
            { headers: { "Content-Type": "application/json" } }
          )
          .then((response) => {
            appointmentObj.roomId = response?.data?.data?.meetingID || null;
          });
      }

      await axios.post(
        PROXY_URL,
        {
          path: `/booking/${lead?.uuid || lead?.lead?.uuid}/${lead?.user?.uuid}/${lead?.taxPro?.user?.uuid || lead?.userpro?.user?.uuid}`,
          method: "POST",
          qa_mode: qa,
          data: appointmentObj,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      setLoading(false);

      if (!isMultyStep) {
        if (auto_login) {
          window.location.href = `${CLIENT_URL}/login-with-token/${userCheck?.user?.verification_token}?email=${userCheck?.user?.email}`;
        } else {
          window.location.href = `${CLIENT_URL}/dashboard`;
        }
      }

      setStatus("success");
      setMessage("Appointment created ✅");
    } catch (error) {
      setLoading(false);
      console.log(error);
      setStatus("error");
      setMessage(error?.response?.data?.message || error?.message || "Failed to book appointment.");
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setServicesLoading(true);
      try {
        const { data } = await axios.post(
          PROXY_URL,
          { path: "/get-services", method: "GET", qa_mode: qa },
          { headers: { "Content-Type": "application/json" } }
        );
        if (active) setServices(Array.isArray(data) ? data : []);
      } catch {
        if (active) setServices([]);
      } finally {
        if (active) setServicesLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [PROXY_URL, qa]);

  const getAvailableTaxPro = async () => {
    try {
      const resp = await axios.post(
        PROXY_URL,
        { path: "/get-available-taxpro", method: "POST", qa_mode: qa, data: { pro_id } },
        { headers: { "Content-Type": "application/json" } }
      );
      setTaxProAvailability({ ...resp?.data?.data?.pro?.user, pro_id: resp?.data?.data?.pro?.id, pro_uuid: resp?.data?.data?.pro?.uuid });
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (Object.keys(userCheck)?.length > 0 && !userCheck?.taxProAssigned) {
      getAvailableTaxPro();
    }
  }, [userCheck]);

  const getAvailability = async () => {
    try {
      setStatus("loading");
      const resp = await axios.post(
        PROXY_URL,
        { path: "/getTaxProAvailabilityByEmail", method: "POST", qa_mode: qa, data: { email } },
        { headers: { "Content-Type": "application/json" } }
      );

      const maybe =
        resp?.data?.data?.taxPro?.user ||
        resp?.data?.data?.taxPro ||
        resp?.data?.data ||
        resp?.data;

      setUserCheck(resp?.data?.data);
      setLead(resp?.data?.data);

      if (resp?.data?.data?.userExist && resp?.data?.data?.leadExists) {
        setTaxProAvailability(maybe || null);
      }
      setStatus("success");
    } catch (err) {
      console.log("BookAppointmentFrom error:", err);
      setStatus("error");
      setMessage(err?.response?.data?.message || err?.message || "Request failed.");
      setTaxProAvailability(null);
    }
  };

  useEffect(() => {
    if (!email) {
      setUserCheck({ userExist: false, leadExists: false });
      setStatus("idle");
      getAvailableTaxPro();
      return;
    }
    getAvailability();
  }, [email, qa, pro_id]);

  const hasPayload =
    !!taxProAvailability &&
    !!taxProAvailability.settings?.timezone &&
    !!taxProAvailability.availability?.time_ranges &&
    typeof taxProAvailability.availability.time_ranges === "object";

  // Keep original protection for today/past selection
  useEffect(() => {
    if (!hasPayload) return;
    const today = new Date();
    const tomorrow = addDays(today, 1);
    const selectedIsPastOrToday =
      new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()) <=
      new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (selectedIsPastOrToday) {
      let found = null;
      for (let i = 0; i < 90; i++) {
        const d = addDays(tomorrow, i);
        const s = buildSlotsForDate(d, taxProAvailability, selectedTZ.value);
        if (s.length > 0) {
          found = d;
          break;
        }
      }
      setSelectedDate(found || tomorrow);
      setSelectedSlotIso(null);
    }
  }, [hasPayload, taxProAvailability, selectedTZ, monthCursor]);

  // Keep monthCursor logic (even though UI doesn’t show calendar now)
  useEffect(() => {
    if (!hasPayload) return;
    const today = new Date();
    let firstAvailable = null;
    for (let i = 1; i <= 90; i++) {
      const d = addDays(today, i);
      const s = buildSlotsForDate(d, taxProAvailability, selectedTZ.value);
      if (s.length > 0) {
        firstAvailable = d;
        break;
      }
    }
    if (firstAvailable && !isSameMonth(firstAvailable, monthCursor)) {
      setMonthCursor(startOfMonth(firstAvailable));
    }
  }, [hasPayload, selectedTZ, taxProAvailability, monthCursor]);

  const LeadCreation = () => (
    <div className="d-flex flex-column gap-3">
      {createLeadSteps === 1 && (
        <BookingSchedulerV2
          hasPayload={hasPayload}
          taxProAvailability={taxProAvailability}
          tzOptions={tzOptions}
          selectedTZ={selectedTZ}
          setSelectedTZ={(opt) => {
            setSelectedTZ(opt);
            setSelectedSlotIso(null); // selecting timezone resets selected time (safe)
          }}
          selectedSlotIso={selectedSlotIso}
          setSelectedSlotIso={setSelectedSlotIso}
          setSelectedDate={setSelectedDate}
          appointmentType={appointmentType}
          setAppointmentType={setAppointmentType}
          loading={loading}
          status={status}
          message={message}
          primaryButtonText="Next"
          onPrimaryClick={() => setCreateLeadSteps(2)}
        />
      )}

      {createLeadSteps === 2 && (
        <div className="container py-3">
          <div className="mb-3">
            {status === "success" && (
              <div className="alert alert-success py-2 mb-2">{message}</div>
            )}
            {status === "error" && (
              <div className="alert alert-danger py-2 mb-2">{message}</div>
            )}
          </div>

          <div className="row g-4">
            <div className="col-12 col-lg-6">
              <h4 className="mb-3 text-dark">What can we help you with?</h4>

              {servicesLoading && <div className="text-muted mb-2">Loading services…</div>}
              {!servicesLoading && services.length === 0 && (
                <div className="text-muted mb-2">No services found.</div>
              )}

              <div className="d-flex flex-column gap-2">
                {services?.map((s) => {
                  const checked = formData.options.includes(String(s.id));
                  return (
                    <label key={s.id} className="text-dark ukf-pointer">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={checked}
                        onChange={(e) => {
                          const id = String(s.id);
                          setFormData((prev) => {
                            const next = new Set(prev.options.map(String));
                            if (e.target.checked) next.add(id);
                            else next.delete(id);
                            return { ...prev, options: Array.from(next) };
                          });
                        }}
                      />
                      {s.name}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <h4 className="mb-3 text-dark">What is your annual income?</h4>
              <div className="d-flex flex-column gap-2">
                {ANNUAL_INCOMES.map((inc) => (
                  <label key={inc.id} className="text-dark ukf-pointer">
                    <input
                      type="radio"
                      name="annual_income"
                      className="form-check-input me-2"
                      checked={String(formData.income) === String(inc.id)}
                      value={inc.id}
                      onChange={(e) => setFormData({ ...formData, income: e.target.value })}
                    />
                    {inc.name}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="zip" className="form-label text-black">
              Zip Code
            </label>
            <input
              id="zip"
              name="zip"
              className="form-control uk-booking-v2__zip"
              placeholder="Enter your Zip Code here"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={8}
              defaultValue={zipRef.current}
              onInput={(e) => {
                const el = e.currentTarget;
                const filtered = el.value.replace(/\D/g, "").slice(0, 8);
                if (filtered !== el.value) {
                  const pos = el.selectionStart || filtered.length;
                  el.value = filtered;
                  requestAnimationFrame(() => el.setSelectionRange(pos, pos));
                }
                zipRef.current = filtered;
              }}
            />
          </div>

          <div className="mt-4">
            <button
              className="btn btn-primary btn-lg rounded-pill px-4"
              onClick={async () => {
                setLoading(true);
                try {
                  const zip = zipRef.current?.value || "";
                  const res = await axios.post(
                    PROXY_URL,
                    {
                      path: "/create-lead-by-email",
                      method: "POST",
                      qa_mode: qa,
                      data: {
                        email,
                        services: formData.options,
                        income: formData.income,
                        pro_id: taxProAvailability?.pro_id || null,
                        zip,
                      },
                    },
                    { headers: { "Content-Type": "application/json" } }
                  );

                  if (res?.data?.success) {
                    await createAppointment(
                      {
                        lead: res.data.data,
                        user: res.data.data?.user,
                        taxPro: res.data.data?.userpro,
                      },
                      false
                    );
                  }
                } catch (err) {
                  console.log(err);
                } finally {
                  setLoading(false);
                }
              }}
              disabled={formData?.options?.length === 0 || !formData?.income}
            >
              {loading && (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              )}
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const ready = userCheck && Object.keys(userCheck).length > 0;

  return (
    <>
      {!ready && (
        <div className="container py-3">
          {status === "error" && <div className="alert alert-danger py-2">Error: {message}</div>}
        </div>
      )}

      {/* Existing user, no lead yet => lead creation flow */}
      {ready && userCheck?.userExist && !userCheck?.leadExists && <LeadCreation />}

      {/* New user => pick time then MultiStepLeadForm */}
      {ready && !userCheck?.userExist && (
        <div>
          {createUserSteps === 1 && (
            <BookingSchedulerV2
              hasPayload={hasPayload}
              taxProAvailability={taxProAvailability}
              tzOptions={tzOptions}
              selectedTZ={selectedTZ}
              setSelectedTZ={(opt) => {
                setSelectedTZ(opt);
                setSelectedSlotIso(null);
              }}
              selectedSlotIso={selectedSlotIso}
              setSelectedSlotIso={setSelectedSlotIso}
              setSelectedDate={setSelectedDate}
              appointmentType={appointmentType}
              setAppointmentType={setAppointmentType}
              loading={loading}
              status={status}
              message={message}
              primaryButtonText="Next"
              onPrimaryClick={() => setCreateUserSteps(2)}
            />
          )}

          {createUserSteps === 2 && (
            <MultiStepLeadForm
              email={email}
              qa_mode={qa}
              clientUrl={CLIENT_URL}
              auto_login={auto_login}
              fromBooking={true}
              createAppointment={createAppointment}
              pro_id={taxProAvailability?.pro_uuid}
              proxyUrl={PROXY_URL}
            />
          )}
        </div>
      )}

      {/* User already has lead and assigned pro => direct booking */}
      {ready && userCheck?.leadExists && userCheck?.taxProAssigned && (
        <BookingSchedulerV2
          hasPayload={hasPayload}
          taxProAvailability={taxProAvailability}
          tzOptions={tzOptions}
          selectedTZ={selectedTZ}
          setSelectedTZ={(opt) => {
            setSelectedTZ(opt);
            setSelectedSlotIso(null);
          }}
          selectedSlotIso={selectedSlotIso}
          setSelectedSlotIso={setSelectedSlotIso}
          setSelectedDate={setSelectedDate}
          appointmentType={appointmentType}
          setAppointmentType={setAppointmentType}
          loading={loading}
          status={status}
          message={message}
          primaryButtonText="Book Appointment"
          onPrimaryClick={() => createAppointment(lead, false)}
        />
      )}
    </>
  );
};

export default BookAppointmentFormUpdatedUi;
